import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, UserRound, KeyRound } from 'lucide-react';

const errText = (e, fallback) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : fallback);

const DetailsCard = () => {
  const { api, user, updateUser } = useAuth();
  const [form, setForm] = useState({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '' });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setBusy(true);
    try {
      const r = await api.put('/auth/me', { first_name: form.first_name.trim(), last_name: form.last_name.trim(), phone: form.phone.trim() });
      updateUser({ ...user, ...r.data });
      toast.success('Profile saved');
    } catch (err) { toast.error(errText(err, 'Could not save your profile')); } finally { setBusy(false); }
  };
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-[#2a3e5d]"><UserRound className="h-5 w-5 text-[#20acf6]" />Your details</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="profile-first-name" /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="profile-last-name" /></div>
          </div>
          <div className="space-y-1.5"><Label>Email</Label><Input value={user?.email || ''} disabled data-testid="profile-email" /><p className="text-xs text-slate-500">Contact us if you need to change your sign-in email.</p></div>
          <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
          <Button type="submit" disabled={busy} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="profile-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save details</Button>
        </form>
      </CardContent>
    </Card>
  );
};

const PasswordCard = () => {
  const { changePassword } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { toast.error('New passwords do not match'); return; }
    if (form.next.length < 8) { toast.error('Use at least 8 characters'); return; }
    setBusy(true);
    try {
      const r = await changePassword(form.current, form.next);
      toast.success(r.message || 'Password changed');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) { toast.error(errText(err, 'Could not change your password')); } finally { setBusy(false); }
  };
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2 text-[#2a3e5d]"><KeyRound className="h-5 w-5 text-[#20acf6]" />Change password</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Current password</Label><Input type="password" autoComplete="current-password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required data-testid="password-current" /></div>
          <div className="space-y-1.5"><Label>New password</Label><Input type="password" autoComplete="new-password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required data-testid="password-new" /><p className="text-xs text-slate-500">At least 8 characters, including a letter and a number.</p></div>
          <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="password-confirm" /></div>
          <Button type="submit" disabled={busy} variant="outline" className="border-[#2a3e5d]/30" data-testid="password-save-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Update password</Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default function ProfilePage() {
  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="profile-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">Profile settings</h1>
          <p className="text-slate-500 mt-1 text-sm">Keep your details up to date — your name appears on your certificates.</p>
        </div>
        <DetailsCard />
        <PasswordCard />
      </div>
    </DashboardLayout>
  );
}
